import { useRef } from 'react';

export interface Product {
  id: string;
  name: string;
  price: string;
  image: string;
  url: string;
}

interface ProductCarouselProps {
  categoryName: string;
  categoryUrl: string;
  products: Product[];
}

export default function ProductCarousel({ categoryName, categoryUrl, products }: ProductCarouselProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = 280;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  return (
    <section className="w-full px-4 md:px-6 lg:px-10 py-6 md:py-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950">
            {categoryName}
          </h2>
          <a
            href={categoryUrl}
            target="_blank"
            rel="noopener nofollow"
            className="text-sm font-semibold text-primary-500 hover:text-primary-600 transition-colors flex items-center gap-1 whitespace-nowrap"
          >
            View All
            <i className="ri-arrow-right-s-line" />
          </a>
        </div>
        <div className="relative group">
          <button
            onClick={() => scroll('left')}
            className="hidden md:flex absolute -left-4 top-1/2 -translate-y-1/2 z-10 w-9 h-9 items-center justify-center bg-background-50/95 rounded-full border border-background-200 text-foreground-700 hover:text-primary-500 transition-colors opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto"
            aria-label="Scroll left"
          >
            <i className="ri-arrow-left-s-line text-xl" />
          </button>
          <div
            ref={scrollRef}
            className="flex gap-3 overflow-x-auto no-scrollbar scroll-smooth pb-2"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {products.map((product) => (
              <a
                key={product.id}
                href={product.url}
                target="_blank"
                rel="noopener nofollow"
                className="group/card shrink-0 w-[160px] sm:w-[180px] md:w-[200px] bg-background-50 rounded-lg overflow-hidden border border-background-200 hover:border-primary-300 transition-all"
              >
                <div className="aspect-square overflow-hidden bg-background-100">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover/card:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-2.5">
                  <h3 className="text-xs font-medium text-foreground-800 line-clamp-2 mb-1.5 group-hover/card:text-primary-500 transition-colors leading-snug">
                    {product.name}
                  </h3>
                  <p className="text-xs font-semibold text-primary-500">
                    {product.price}
                  </p>
                </div>
              </a>
            ))}
          </div>
          <button
            onClick={() => scroll('right')}
            className="hidden md:flex absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-9 h-9 items-center justify-center bg-background-50/95 rounded-full border border-background-200 text-foreground-700 hover:text-primary-500 transition-colors opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto"
            aria-label="Scroll right"
          >
            <i className="ri-arrow-right-s-line text-xl" />
          </button>
        </div>
      </div>
    </section>
  );
}